// Storage adapter: no DOM, cart writes or catalog requests. In-memory fallback
// keeps compare usable when browser privacy/quota settings reject persistence.
function createCompareStorage({ load, save, normalize }) {
  let memory = [];
  let volatile = false;
  const copy = value => JSON.parse(JSON.stringify(value));
  return {
    read() {
      if (!volatile) {
        try {
          const raw = load();
          const parsed = raw ? JSON.parse(raw) : [];
          memory = normalize(Array.isArray(parsed) ? parsed : []);
        } catch (_) { /* Retain the last usable state on inaccessible storage. */ }
      }
      return copy(memory);
    },
    write(items) {
      memory = normalize(copy(items));
      try { save(JSON.stringify(memory)); volatile = false; }
      catch (_) { volatile = true; }
    },
    receive(raw) {
      try {
        const parsed = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(parsed)) return;
        memory = normalize(parsed);
        volatile = false;
      } catch (_) { /* Ignore corrupt cross-tab messages. */ }
    },
  };
}

/*
 * Product comparison - storewide compare set + floating tray + <compare-table>.
 *
 * Data model: the compare set lives in localStorage, keyed by product handle (one
 * entry per PRODUCT - item_group_id was too coarse and made sibling colours/sizes
 * and even different MacBook models collide). The compare page builds one column per
 * handle by fetching the `compare-column` section via the Section Rendering API;
 * color/storage switches inside a column refetch it via extra_options. Kept
 * dependency-free and idempotent (safe to load twice).
 */
(function () {
  if (window.__alasilCompareInit) return;
  window.__alasilCompareInit = true;

  var KEY = 'alasil:compare';
  var MAX = 4;
  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var COMPARE_PAGE = root + 'pages/compare';
  var tray = document.querySelector('[data-compare-tray]');
  var trayAutoCollapseTimer = 0;
  var trayExpanded = false;

  function text(key, fallback) {
    return (tray && tray.dataset[key]) || fallback;
  }
  function interpolate(template, values) {
    return Object.keys(values).reduce(function (result, key) {
      return result.split('{' + key + '}').join(values[key]).split('%' + key + '%').join(values[key]);
    }, template || '');
  }
  function announce(message) {
    var live = tray && tray.querySelector('[data-compare-live]');
    if (!live) return;
    live.textContent = '';
    window.setTimeout(function () { live.textContent = message; }, 20);
  }
  function emit(name, item, count) {
    document.dispatchEvent(new CustomEvent(name, {
      detail: {
        id: item && item.id || '',
        product_id: item && item.productId || '',
        handle: item && item.handle || '',
        group: item && item.group || '',
        type: item && item.type || '',
        count: count
      }
    }));
  }

  // Migrate pre-family entries that stored a narrow taxonomy leaf (for example
  // "Laptops") in localStorage. Product titles are enough to recover the stable
  // purchase-decision family, so a saved MacBook can immediately compare with a
  // Mac mini after this release instead of producing a false category conflict.
  function familyFor(item) {
    var explicit = String(item && (item.family || item.type) || '').toLowerCase();
    var known = {
      mac: text('familyMac', 'Mac'),
      iphone: text('familyIphone', 'iPhone'),
      ipad: text('familyIpad', 'iPad'),
      'apple-watch': text('familyAppleWatch', 'Apple Watch'),
      'personal-audio': text('familyPersonalAudio', 'Personal audio'),
      display: text('familyDisplay', 'Displays')
    };
    if (known[explicit]) return { key: explicit, label: known[explicit] };

    var categoryContext = [item && item.type, item && item.typeLabel].filter(Boolean).join(' ').toLowerCase();
    var isDeviceAccessory = /case|cover|keyboard|stylus|cable|adapter|charger|watch band|strap|screen protector|sleeve/.test(categoryContext);
    if (isDeviceAccessory) {
      return { key: item && item.type || '', label: item && item.typeLabel || item && item.type || '' };
    }

    var haystack = [item && item.title, item && item.modelTitle, item && item.typeLabel, item && item.handle, item && item.id]
      .filter(Boolean).join(' ').toLowerCase();
    if (/macbook|mac mini|mac studio|\bimac\b|\bmac pro\b/.test(haystack)) return { key: 'mac', label: known.mac };
    if (/\biphone\b/.test(haystack)) return { key: 'iphone', label: known.iphone };
    if (/\bipad\b/.test(haystack)) return { key: 'ipad', label: known.ipad };
    if (/apple watch/.test(haystack)) return { key: 'apple-watch', label: known['apple-watch'] };
    if (/airpods|\bbeats\b/.test(haystack)) return { key: 'personal-audio', label: known['personal-audio'] };
    if (/studio display|pro display/.test(haystack)) return { key: 'display', label: known.display };
    return { key: item && item.type || '', label: item && item.typeLabel || item && item.type || '' };
  }

  /* ---------------------------------- store --------------------------------- */
  const compareStorage = createCompareStorage({
    load: () => localStorage.getItem(KEY),
    save: value => localStorage.setItem(KEY, value),
    normalize: stored => stored.filter(item => item && typeof item.id === 'string' && typeof item.handle === 'string')
      .slice(0, MAX).map(item => {
        const family = familyFor(item);
        return family.key ? Object.assign({}, item, { type: family.key, typeLabel: family.label, family: family.key }) : item;
      }),
  });
  function read() { return compareStorage.read(); }
  function write(arr) {
    compareStorage.write(arr);
    document.dispatchEvent(new CustomEvent('compare:change'));
  }
  function has(id) { return read().some(function (x) { return x.id === id; }); }
  function remove(id) {
    var arr = read();
    var item = arr.filter(function (x) { return x.id === id; })[0];
    write(arr.filter(function (x) { return x.id !== id; }));
    if (item) {
      announce(interpolate(text('removedTemplate', '{product} removed from comparison.'), { product: item.title || item.modelTitle || '' }));
      emit('wg:compare_remove', item, Math.max(0, arr.length - 1));
    }
  }
  function clearAll() { write([]); }
  function add(item) {
    var arr = read();
    if (arr.some(function (x) { return x.id === item.id; })) return { ok: true };
    // Same-family guard: products may cross taxonomy leaves when they participate in
    // the same purchase decision (MacBook vs Mac mini), but unrelated families remain
    // blocked (Mac vs iPhone). Legacy entries with no family are ignored by the check.
    if (item.type && arr.length) {
      var clash = arr.filter(function (x) { return x.type && x.type !== item.type; })[0];
      if (clash) return { ok: false, reason: 'type', type: clash.type, typeLabel: clash.typeLabel || clash.type };
    }
    if (arr.length >= MAX) return { ok: false, reason: 'max' };
    arr.push(item);
    expandTrayTemporarily();
    write(arr);
    announce(interpolate(text('addedTemplate', '{product} added to comparison.'), { product: item.title || item.modelTitle || '' }));
    emit('wg:compare_add', item, arr.length);
    return { ok: true };
  }

  /* -------------------------------- buttons --------------------------------- */
  function syncButtons() {
    var set = read();
    var selectedFamily = set.filter(function (item) { return item.type; })[0];
    document.querySelectorAll('[data-compare-toggle]').forEach(function (btn) {
      var id = btn.dataset.id;
      var on = set.some(function (x) { return x.id === id; });
      var incompatible = !on && selectedFamily && btn.dataset.type && selectedFamily.type !== btn.dataset.type;
      btn.classList.toggle('is-active', on);
      btn.classList.toggle('cmp-compare-btn--incompatible', !!incompatible);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.classList.remove('cmp-compare-btn--disabled');
      btn.disabled = false;
      var label = on ? btn.dataset.labelOn : btn.dataset.labelOff;
      if (incompatible) {
        label = interpolate(text('sameCategory', '%product_family% products cannot be compared with %selected_family% products. Clear the current comparison or choose another %selected_family% product.'), {
          product_family: btn.dataset.typeLabel || btn.dataset.type,
          selected_family: selectedFamily.typeLabel || selectedFamily.type
        });
      }
      if (label) {
        btn.setAttribute('aria-label', label);
        btn.setAttribute('title', label);
      }
    });
  }

  // Capture phase + stopPropagation: product cards wrap the image in a link, so the
  // toggle must intercept the click before it bubbles to that link and navigates.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-compare-toggle]');
    if (btn) {
      e.preventDefault();
      e.stopPropagation();
      var id = btn.dataset.id;
      if (!id) return;
      if (has(id)) { remove(id); return; }
      var res = add({
        id: id,
        productId: btn.dataset.analyticsProductId,
        handle: btn.dataset.handle,
        title: btn.dataset.title || '',
        modelTitle: btn.dataset.modelTitle || btn.dataset.title || '',
        image: btn.dataset.image || '',
        url: btn.dataset.url || '',
        type: btn.dataset.type || '',
        typeLabel: btn.dataset.typeLabel || btn.dataset.type || '',
        family: btn.dataset.comparisonFamily || btn.dataset.type || '',
        group: btn.dataset.group || ''
      });
      if (!res.ok) {
        if (res.reason === 'type') {
          toast(interpolate(text('sameCategory', '%product_family% products cannot be compared with %selected_family% products. Clear the current comparison or choose another %selected_family% product.'), {
            product_family: btn.dataset.typeLabel || btn.dataset.type || '',
            selected_family: res.typeLabel || res.type || ''
          }));
        }
        else toast(interpolate(text('limitTemplate', 'You can compare up to {max} products.'), { max: MAX }));
      }
      return;
    }
    var rm = e.target.closest('[data-compare-remove-id]');
    if (rm) { e.preventDefault(); e.stopPropagation(); remove(rm.getAttribute('data-compare-remove-id')); return; }
    if (e.target.closest('[data-compare-clear]')) { e.preventDefault(); e.stopPropagation(); clearAll(); }
    var panelToggle = e.target.closest('[data-compare-panel-toggle]');
    if (panelToggle) {
      e.preventDefault();
      e.stopPropagation();
      setTrayExpanded(!trayExpanded, true);
      return;
    }
    var go = e.target.closest('[data-compare-go]');
    if (go && go.getAttribute('aria-disabled') === 'true') {
      e.preventDefault();
      toast(text('addOne', 'Add one more product'));
    } else if (go) {
      emit('wg:compare_open', null, read().length);
    }
  }, true);

  // Product cards navigate via pointer/touch tap handlers (card slider), so a click-only
  // guard isn't enough on mobile - swallow the pointer/touch sequence on the compare
  // controls before the card sees it. stopPropagation only (no preventDefault) so the
  // button still produces its click.
  ['pointerdown', 'mousedown', 'touchstart', 'pointerup', 'touchend'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      var t = e.target;
      if (t && t.closest && t.closest('[data-compare-toggle], [data-compare-remove-id], [data-compare-clear], [data-compare-panel-toggle]')) {
        e.stopPropagation();
      }
    }, true);
  });

  /* ------------------------------ jump nav ---------------------------------- */
  // Compare-page chips scroll the table to a spec group (the group header lives in
  // the sticky label column, tagged data-grouprow). Lives at document level since the
  // chips sit outside the <compare-table> element.
  document.addEventListener('click', function (e) {
    var jb = e.target.closest && e.target.closest('[data-compare-jump]');
    if (!jb) return;
    var id = jb.getAttribute('data-compare-jump');
    var target = document.querySelector('.cmp-col--labels [data-grouprow="' + id + '"]');
    if (target) {
      var y = target.getBoundingClientRect().top + window.scrollY - 110;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  });

  /* --------------------------------- tray ----------------------------------- */
  function setTrayExpanded(expanded, userInitiated) {
    if (!tray) return;
    var panel = tray.querySelector('[data-compare-panel]');
    var toggle = tray.querySelector('[data-compare-panel-toggle]');
    trayExpanded = !!expanded;
    tray.classList.toggle('is-expanded', trayExpanded);
    if (panel) panel.hidden = !trayExpanded;
    if (toggle) {
      toggle.setAttribute('aria-expanded', trayExpanded ? 'true' : 'false');
      toggle.setAttribute('aria-label', trayExpanded ? text('collapseLabel', 'Hide selected products') : text('expandLabel', 'Show selected products'));
    }
    if (userInitiated || !trayExpanded) {
      window.clearTimeout(trayAutoCollapseTimer);
      trayAutoCollapseTimer = 0;
    }
  }

  function expandTrayTemporarily() {
    if (!tray) return;
    setTrayExpanded(true, false);
    window.clearTimeout(trayAutoCollapseTimer);
    trayAutoCollapseTimer = window.setTimeout(function () {
      var panel = tray.querySelector('[data-compare-panel]');
      if (panel && panel.contains(document.activeElement)) return;
      setTrayExpanded(false, false);
    }, 2800);
  }

  function renderTray() {
    var set = read();
    if (!tray) return;
    var items = tray.querySelector('[data-compare-items]');
    var status = tray.querySelector('[data-compare-status]');
    var count = tray.querySelector('[data-compare-count]');
    var go = tray.querySelector('[data-compare-go]');
    if (!set.length) {
      setTrayExpanded(false, false);
      tray.hidden = true;
      tray.classList.remove('is-visible');
      tray.setAttribute('aria-hidden', 'true');
      if (items) items.replaceChildren();
      return;
    }

    items.replaceChildren();
    set.forEach(function (x) {
      var it = document.createElement('article');
      it.className = 'cmp-tray__item';
      it.setAttribute('role', 'listitem');
      if (x.image) {
        var img = document.createElement('img');
        img.src = x.image; img.alt = ''; img.loading = 'lazy'; img.width = 52; img.height = 52;
        it.appendChild(img);
      }
      var copy = document.createElement('span');
      copy.className = 'cmp-tray__item-copy';
      var name = document.createElement('span');
      name.className = 'cmp-tray__item-title';
      name.textContent = x.title || x.modelTitle || '';
      copy.appendChild(name);
      if (x.typeLabel) {
        var category = document.createElement('span');
        category.className = 'cmp-tray__item-category';
        category.textContent = x.typeLabel;
        copy.appendChild(category);
      }
      it.appendChild(copy);
      var rm = document.createElement('button');
      rm.type = 'button'; rm.className = 'cmp-tray__rm';
      rm.setAttribute('data-compare-remove-id', x.id);
      rm.setAttribute('aria-label', interpolate(text('removeTemplate', 'Remove {product}'), { product: x.title || x.modelTitle || '' }));
      rm.innerHTML = '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="m4.5 4.5 7 7m0-7-7 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
      it.appendChild(rm);
      items.appendChild(it);
    });
    var statusText = interpolate(text('statusTemplate', '{count} of {max} selected'), { count: set.length, max: MAX });
    status.textContent = statusText + ' · ' + (set.length < 2 ? text('addOne', 'Add one more product') : text('ready', 'Ready to compare'));
    count.textContent = set.length;
    go.href = (tray.dataset.comparePage || COMPARE_PAGE) + '?products=' + set.map(function (x) { return encodeURIComponent(x.handle); }).join(',');
    var disabled = set.length < 2;
    go.classList.toggle('is-disabled', disabled);
    go.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    if (disabled) go.setAttribute('tabindex', '-1'); else go.removeAttribute('tabindex');
    tray.hidden = false;
    tray.setAttribute('aria-hidden', 'false');
    tray.classList.add('is-visible');
  }

  if (tray) {
    tray.addEventListener('focusin', function () {
      window.clearTimeout(trayAutoCollapseTimer);
      trayAutoCollapseTimer = 0;
    });
    tray.addEventListener('pointerdown', function () {
      window.clearTimeout(trayAutoCollapseTimer);
      trayAutoCollapseTimer = 0;
    });
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || !trayExpanded || tray.hidden) return;
      var focusWasInside = tray.contains(document.activeElement);
      setTrayExpanded(false, true);
      if (focusWasInside) {
        var toggle = tray.querySelector('[data-compare-panel-toggle]');
        if (toggle) toggle.focus();
      }
    });
  }

  function toast(msg) {
    var t = document.querySelector('.cmp-toast');
    if (!t) {
      t = document.createElement('div');
      t.className = 'cmp-toast';
      t.setAttribute('role', 'status');
      t.setAttribute('aria-live', 'polite');
      t.setAttribute('aria-atomic', 'true');
      document.body.appendChild(t);
    }
    var visibleTray = document.querySelector('[data-compare-tray].is-visible');
    if (visibleTray) {
      var trayTop = visibleTray.getBoundingClientRect().top;
      t.style.setProperty('--cmp-toast-bottom', Math.max(16, Math.round(window.innerHeight - trayTop + 12)) + 'px');
    } else {
      t.style.removeProperty('--cmp-toast-bottom');
    }
    t.textContent = msg; t.classList.add('is-visible');
    announce(msg);
    clearTimeout(t._h); t._h = setTimeout(function () { t.classList.remove('is-visible'); }, 2600);
  }

  document.addEventListener('compare:change', function () { syncButtons(); renderTray(); });
  window.addEventListener('storage', function (e) { if (e.key === KEY) { compareStorage.receive(e.newValue); syncButtons(); renderTray(); } });

  var syncQueued = false;
  function queueButtonSync() {
    if (syncQueued) return;
    syncQueued = true;
    window.requestAnimationFrame(function () {
      syncQueued = false;
      syncButtons();
    });
  }

  // Recommendation rails are section-rendered after initial page load. Keep newly
  // inserted compare controls in sync with the saved family immediately, so an
  // incompatible choice explains itself before (and again after) it is activated.
  var compareButtonObserver = new MutationObserver(function (records) {
    var hasNewCompareButton = records.some(function (record) {
      return Array.prototype.some.call(record.addedNodes, function (node) {
        return node.nodeType === 1 && (node.matches('[data-compare-toggle]') || node.querySelector('[data-compare-toggle]'));
      });
    });
    if (hasNewCompareButton) queueButtonSync();
  });

  function boot() {
    syncButtons();
    renderTray();
    compareButtonObserver.observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  /* ----------------------------- column fetch ------------------------------- */
  function fetchColumn(handle) {
    var url = root + 'products/' + encodeURIComponent(handle) + '?section_id=compare-column';
    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!html) return null;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.querySelector('.cmp-col--product');
      })
      .catch(function () { return null; });
  }

  /* --------------------------- <compare-table> ------------------------------ */
  var CompareTable = (function () {
    function CT() { return Reflect.construct(HTMLElement, [], CT); }
    CT.prototype = Object.create(HTMLElement.prototype);
    CT.prototype.constructor = CT;

    CT.prototype.connectedCallback = function () {
      if (this._init) return; this._init = true;
      var cfg = { max: MAX, models: [] };
      var cfgEl = this.querySelector('[data-compare-config]');
      if (cfgEl) { try { cfg = Object.assign(cfg, JSON.parse(cfgEl.textContent)); } catch (e) {} }
      cfg.models = (cfg.models || []).map(function (model) {
        var family = familyFor({
          handle: model.handle,
          title: model.label,
          type: model.type,
          typeLabel: model.type
        });
        model.type = family.key;
        model.typeLabel = family.label;
        return model;
      });
      this.cfg = cfg;
      this.max = parseInt(this.dataset.max, 10) || cfg.max || MAX;
      this.slots = this.querySelector('[data-compare-slots]');
      this.empty = this.querySelector('[data-compare-empty]');

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('pointerover', this.onRowHover.bind(this));
      this.addEventListener('pointerout', this.onRowHover.bind(this));
      this.addEventListener('pointerdown', this.onDragStart.bind(this));
      this.addEventListener('keydown', this.onReorderKeydown.bind(this));
      var diff = document.querySelector('[data-compare-diff]');
      if (diff) {
        var self = this;
        // Pill is a <button> toggling aria-pressed; legacy checkbox fires 'change'.
        diff.addEventListener('click', function () {
          if (diff.tagName === 'BUTTON') {
            var on = diff.getAttribute('aria-pressed') !== 'true';
            diff.setAttribute('aria-pressed', on ? 'true' : 'false');
          }
          self.afterRender();
        });
        diff.addEventListener('change', this.afterRender.bind(this));
      }
      var ct = this;
      this._onResize = debounce(function () { ct.setTableWidth(); ct.syncRowHeights(); ct.updateSticky(); }, 150);
      window.addEventListener('resize', this._onResize);

      this.setupSticky();
      this.init();
    };

    /* --------------------- sticky condensed column header --------------------- */
    CT.prototype.setupSticky = function () {
      if (this._stickyBar) return;
      var bar = document.createElement('div');
      bar.className = 'cmp-stickybar';
      this.appendChild(bar); // inside .cmp-wrap so design tokens resolve
      this._stickyBar = bar;
      var self = this;
      this._onSticky = function () {
        if (self._stickyRaf) return;
        self._stickyRaf = requestAnimationFrame(function () { self._stickyRaf = null; self.updateSticky(); });
      };
      window.addEventListener('scroll', this._onSticky, { passive: true });
      var grid = this.querySelector('.cmp-grid');
      if (grid) grid.addEventListener('scroll', this._onSticky, { passive: true });
    };

    // Rebuild one cell per product column (mini image + model name) + a label-column mask.
    CT.prototype.renderSticky = function () {
      var bar = this._stickyBar; if (!bar) return;
      bar.innerHTML = '';
      var labelCol = this.querySelector('.cmp-col--labels');
      if (labelCol) {
        var mask = document.createElement('div');
        mask.className = 'cmp-stickybar__mask';
        mask._track = labelCol;
        bar.appendChild(mask);
      }
      this.productColumns().forEach(function (col) {
        var slot = col.closest('.cmp-slot');
        var img = col.querySelector('.cmp-col__media img');
        var modelNameElement = col.querySelector('.cmp-col__model-name') || col.querySelector('.cmp-col__name');
        var modelName = col.dataset.modelName || (modelNameElement ? modelNameElement.textContent.trim() : '');
        var configuration = col.dataset.configuration || '';
        var cell = document.createElement('div');
        cell.className = 'cmp-stickybar__cell';
        cell._track = slot;
        var html = '';
        if (img) { html += '<img class="cmp-stickybar__img" src="' + (img.currentSrc || img.src) + '" alt="" aria-hidden="true">'; }
        html += '<span class="cmp-stickybar__name">' + modelName + (configuration ? ' · ' + configuration : '') + '</span>';
        cell.innerHTML = html;
        bar.appendChild(cell);
      });
      this.updateSticky();
    };

    CT.prototype.updateSticky = function () {
      var bar = this._stickyBar; if (!bar) return;
      var header = this.querySelector('.cmp-col--product .cmp-col__header');
      var grid = this.querySelector('.cmp-grid');
      if (!header || !grid) { bar.classList.remove('is-visible'); return; }
      var gridRect = grid.getBoundingClientRect();
      // Show once the product headers have scrolled above the top, while the table is
      // still on screen.
      var show = header.getBoundingClientRect().bottom < 4 && gridRect.bottom > 80;
      bar.classList.toggle('is-visible', show);
      if (!show) return;
      var cells = bar.children;
      for (var i = 0; i < cells.length; i++) {
        var c = cells[i];
        if (!c._track) continue;
        var r = c._track.getBoundingClientRect();
        c.style.left = Math.round(r.left) + 'px';
        c.style.width = Math.round(r.width) + 'px';
      }
    };

    CT.prototype.initialHandles = function () {
      var params = new URLSearchParams(location.search);
      var p = params.get('products');
      if (p) {
        var requestedHandles = p.split(',').map(decodeURIComponent).filter(Boolean);
        // The MacBook Pro landing page seeds current M5 tiers from live linked
        // options. A screen-size filter can legitimately contain no M4 product,
        // so complete the fourth slot from the merchant-managed comparison list.
        // This is semantic selection only; no product handle is hard-coded.
        if (params.get('preset') === 'macbook-pro-chip-tiers' && requestedHandles.length < this.max) {
          var requestedCollectionForPreset = String(params.get('collection') || '').trim().toLowerCase();
          var previousGeneration = (this.cfg.models || []).find(function (model) {
            if (!/\bm4\b/i.test(String(model.label || ''))) return false;
            if (!requestedCollectionForPreset) return true;
            return Array.isArray(model.collections) && model.collections.some(function (handle) {
              return String(handle || '').toLowerCase() === requestedCollectionForPreset;
            });
          });
          if (previousGeneration && requestedHandles.indexOf(previousGeneration.handle) === -1) {
            requestedHandles.push(previousGeneration.handle);
          }
        }
        return requestedHandles;
      }
      // A family-scoped entry point must not inherit an unrelated saved comparison.
      // This lets collection landing pages open a coherent comparison without
      // hard-coding product handles that may later be replaced or unpublished.
      var requestedFamily = String(params.get('family') || '').trim().toLowerCase();
      var requestedCollection = String(params.get('collection') || '').trim().toLowerCase();
      if (requestedFamily || requestedCollection) {
        return (this.cfg.models || []).slice()
          .filter(function (model) {
            if (requestedFamily && model.type !== requestedFamily) return false;
            if (!requestedCollection) return true;
            return Array.isArray(model.collections) && model.collections.some(function (handle) {
              return String(handle || '').toLowerCase() === requestedCollection;
            });
          })
          .sort(function (a, b) {
            return (parseInt(b.releaseYear, 10) || 0) - (parseInt(a.releaseYear, 10) || 0);
          })
          .slice(0, this.max)
          .map(function (model) { return model.handle; });
      }
      var set = read();
      if (set.length) return set.map(function (x) { return x.handle; });
      // No products picked yet: default to the first few models of one category
      // (the first model's category) so the default view is a coherent comparison.
      var models = this.cfg.models || [];
      if (!models.length) return [];
      var firstType = models[0].type;
      return models
        .filter(function (m) { return !firstType || m.type === firstType; })
        .slice(0, this.max)
        .map(function (m) { return m.handle; });
    };

    CT.prototype.init = function () {
      var self = this;
      var seenHandles = {};
      var handles = this.initialHandles().filter(function (handle) {
        if (!handle || seenHandles[handle]) return false;
        seenHandles[handle] = true;
        return true;
      }).slice(0, this.max);
      if (!handles.length) { this.empty.hidden = false; this.ensureAddSlot(); return; }
      this.empty.hidden = true;
      // Each fetched column calls afterRender() as it arrives. During the initial
      // progressive load that is only a partial view of the requested selection,
      // so it must never overwrite the complete URL/localStorage state.
      this._initializingColumns = true;
      var initialFetchFailed = false;
      // Load columns one at a time so the Section Rendering API isn't hit with a
      // burst (kinder to rate limits; columns appear progressively).
      handles.reduce(function (chain, h) {
        return chain.then(function () {
          return self.addColumn(h).then(function (result) {
            if (result === 'failed') initialFetchFailed = true;
          });
        });
      }, Promise.resolve()).then(function () {
        self._initializingColumns = false;
        self.ensureAddSlot();
        // If a transient request failed, preserve the requested URL/tray instead
        // of silently deleting the missing product. A later explicit interaction
        // can still establish a new complete selection.
        self.afterRender({ syncState: !initialFetchFailed });
      });
    };

    CT.prototype.buildSelect = function (placeholder) {
      var sel = document.createElement('select');
      sel.className = 'cmp-slot__select';
      sel.setAttribute('data-compare-model', '');
      sel.setAttribute('tabindex', '-1');
      sel.setAttribute('aria-hidden', 'true');
      if (placeholder) {
        // "+ Add model" is a PROMPT, not a real choice: make it disabled + selected so
        // it labels the empty pill but can't be picked (and shows greyed, not ticked, in
        // the native iOS picker) - picking happens from the real model options below.
        var o = document.createElement('option');
        o.value = ''; o.textContent = placeholder;
        o.disabled = true; o.selected = true;
        sel.appendChild(o);
      }
      (this.cfg.models || []).forEach(function (m) {
        var opt = document.createElement('option');
        opt.value = m.handle; opt.dataset.id = m.id; opt.dataset.type = m.type || ''; opt.textContent = m.label;
        sel.appendChild(opt);
      });
      return sel;
    };

    CT.prototype.buildModelTrigger = function (placeholder) {
      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'cmp-model-trigger';
      trigger.setAttribute('data-compare-model-trigger', '');
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', 'false');
      var label = document.createElement('span');
      label.className = 'cmp-model-trigger__label';
      label.textContent = placeholder || (this.cfg.labels && this.cfg.labels.chooseModel) || 'Choose a model';
      trigger.appendChild(label);
      return trigger;
    };

    CT.prototype.syncModelTrigger = function (slot) {
      if (!slot) return;
      var select = slot.querySelector('[data-compare-model]');
      var trigger = slot.querySelector('[data-compare-model-trigger]');
      if (!select || !trigger) return;
      var option = select.options[select.selectedIndex];
      var fallback = slot.classList.contains('cmp-slot--empty')
        ? '+ ' + (this.cfg.labels && this.cfg.labels.addModel || 'Add model')
        : (this.cfg.labels && this.cfg.labels.chooseModel || 'Choose a model');
      var label = option && option.value !== '__loading__' ? option.textContent.trim() : (option && option.value === '__loading__' ? '…' : fallback);
      var labelNode = trigger.querySelector('.cmp-model-trigger__label');
      if (labelNode) labelNode.textContent = label;
      trigger.title = label;
      trigger.setAttribute('aria-label', label);
    };

    CT.prototype.ensureModelDialog = function () {
      if (this._modelDialog) return this._modelDialog;
      var labels = this.cfg.labels || {};
      var dialog = document.createElement('dialog');
      dialog.className = 'cmp-model-dialog';
      dialog.setAttribute('aria-labelledby', 'cmp-model-dialog-title');

      var shell = document.createElement('div');
      shell.className = 'cmp-model-dialog__shell';
      var header = document.createElement('header');
      header.className = 'cmp-model-dialog__header';
      var title = document.createElement('h2');
      title.id = 'cmp-model-dialog-title';
      title.textContent = labels.chooseModel || 'Choose a model';
      var close = document.createElement('button');
      close.type = 'button';
      close.className = 'cmp-model-dialog__close';
      close.setAttribute('data-compare-model-close', '');
      close.textContent = labels.close || 'Close';
      header.appendChild(title); header.appendChild(close);

      var searchWrap = document.createElement('label');
      searchWrap.className = 'cmp-model-dialog__search';
      var searchText = document.createElement('span');
      searchText.className = 'visually-hidden';
      searchText.textContent = labels.searchModels || 'Search models';
      var search = document.createElement('input');
      search.type = 'search';
      search.autocomplete = 'off';
      search.placeholder = labels.searchModels || 'Search models';
      search.setAttribute('data-compare-model-search', '');
      searchWrap.appendChild(searchText); searchWrap.appendChild(search);

      var list = document.createElement('div');
      list.className = 'cmp-model-dialog__list';
      list.setAttribute('data-compare-model-list', '');
      var empty = document.createElement('p');
      empty.className = 'cmp-model-dialog__empty';
      empty.hidden = true;
      empty.textContent = labels.noModels || 'No matching models';
      empty.setAttribute('data-compare-model-empty', '');

      shell.appendChild(header); shell.appendChild(searchWrap); shell.appendChild(list); shell.appendChild(empty);
      dialog.appendChild(shell);
      this.appendChild(dialog);
      this._modelDialog = dialog;

      var self = this;
      close.addEventListener('click', function () { dialog.close(); });
      search.addEventListener('input', function () { self.filterModelDialog(search.value); });
      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) dialog.close();
        var choice = event.target.closest('[data-compare-model-choice]');
        if (!choice || !self._activeModelSelect) return;
        self._activeModelSelect.value = choice.getAttribute('data-compare-model-choice');
        self._activeModelSelect.dispatchEvent(new Event('change', { bubbles: true }));
        dialog.close();
      });
      dialog.addEventListener('close', function () {
        if (self._activeModelTrigger) {
          self._activeModelTrigger.setAttribute('aria-expanded', 'false');
          self._activeModelTrigger.focus({ preventScroll: true });
        }
        self._activeModelSelect = null;
        self._activeModelTrigger = null;
      });
      return dialog;
    };

    CT.prototype.filterModelDialog = function (query) {
      var dialog = this._modelDialog;
      if (!dialog) return;
      var needle = String(query || '').trim().toLowerCase();
      var visible = 0;
      dialog.querySelectorAll('[data-compare-model-choice]').forEach(function (choice) {
        var show = !needle || choice.textContent.toLowerCase().indexOf(needle) !== -1;
        choice.hidden = !show;
        if (show) visible++;
      });
      var empty = dialog.querySelector('[data-compare-model-empty]');
      if (empty) empty.hidden = visible > 0;
    };

    CT.prototype.openModelPicker = function (trigger) {
      var slot = trigger && trigger.closest('.cmp-slot');
      var select = slot && slot.querySelector('[data-compare-model]');
      if (!select) return;
      var dialog = this.ensureModelDialog();
      var list = dialog.querySelector('[data-compare-model-list]');
      var search = dialog.querySelector('[data-compare-model-search]');
      var labels = this.cfg.labels || {};
      list.replaceChildren();
      var modelByHandle = {};
      (this.cfg.models || []).forEach(function (model) { modelByHandle[model.handle] = model; });
      [].slice.call(select.options).forEach(function (option) {
        if (!option.value || option.value === '__loading__') return;
        var model = modelByHandle[option.value] || {};
        var choice = document.createElement('button');
        choice.type = 'button';
        choice.className = 'cmp-model-choice';
        choice.setAttribute('data-compare-model-choice', option.value);
        if (option.selected) choice.setAttribute('aria-current', 'true');
        if (model.image) {
          var image = document.createElement('img');
          image.src = model.image;
          image.alt = '';
          image.loading = 'lazy';
          image.width = 56; image.height = 56;
          choice.appendChild(image);
        }
        var copy = document.createElement('span');
        copy.className = 'cmp-model-choice__copy';
        var name = document.createElement('span');
        name.className = 'cmp-model-choice__name';
        name.textContent = option.textContent.trim();
        copy.appendChild(name);
        if (option.selected) {
          var current = document.createElement('span');
          current.className = 'cmp-model-choice__current';
          current.textContent = labels.currentModel || 'Current model';
          copy.appendChild(current);
        }
        choice.appendChild(copy);
        list.appendChild(choice);
      });
      this._activeModelSelect = select;
      this._activeModelTrigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');
      search.value = '';
      this.filterModelDialog('');
      if (dialog.open) dialog.close();
      dialog.showModal();
      window.setTimeout(function () { search.focus({ preventScroll: true }); }, 20);
    };

    CT.prototype.makeSlot = function (empty) {
      var slot = document.createElement('div');
      slot.className = 'cmp-slot' + (empty ? ' cmp-slot--empty' : '');
      // Drag handle (grip) - hidden by CSS while the slot is the empty "+ Add model"
      // one, shown once a product is loaded. Reorders the column on pointer drag.
      var handle = document.createElement('button');
      handle.type = 'button';
      handle.className = 'cmp-slot__drag';
      handle.setAttribute('data-compare-drag', '');
      var dragLabel = this.cfg.labels && (this.cfg.labels.reorderInstructions || this.cfg.labels.dragToReorder) || 'Reorder column. Use the Left and Right Arrow keys.';
      handle.setAttribute('aria-label', dragLabel);
      handle.setAttribute('title', this.cfg.labels && this.cfg.labels.dragToReorder || 'Drag to reorder');
      handle.innerHTML = '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><g fill="currentColor"><circle cx="6" cy="4" r="1.4"/><circle cx="10" cy="4" r="1.4"/><circle cx="6" cy="8" r="1.4"/><circle cx="10" cy="8" r="1.4"/><circle cx="6" cy="12" r="1.4"/><circle cx="10" cy="12" r="1.4"/></g></svg>';
      var picker = document.createElement('div');
      picker.className = 'cmp-slot__picker';
      var addModelLabel = this.cfg.labels && this.cfg.labels.addModel || 'Add model';
      var prompt = empty ? '+ ' + addModelLabel : null;
      picker.appendChild(this.buildSelect(prompt));
      picker.appendChild(this.buildModelTrigger(prompt));
      var body = document.createElement('div');
      body.className = 'cmp-slot__body';
      slot.appendChild(handle); slot.appendChild(picker); slot.appendChild(body);
      this.syncModelTrigger(slot);
      return slot;
    };

    CT.prototype.productColumns = function () {
      return [].slice.call(this.querySelectorAll('.cmp-slot .cmp-col--product'));
    };

    CT.prototype.addColumn = function (handle) {
      if (this.productColumns().length >= this.max) return Promise.resolve();
      var slot = this.makeSlot(false);
      this.slots.appendChild(slot);
      return this.loadInto(slot, handle);
    };

    CT.prototype.loadInto = function (slot, handle) {
      var self = this;
      var body = slot.querySelector('.cmp-slot__body');
      slot.classList.remove('cmp-slot--empty');
      body.classList.add('is-loading');
      // The column fetches async; until it arrives, show the right model in the pill (if
      // it's a known one) instead of flashing the first config option (e.g. "AirPods 4").
      // Unknown handles get a neutral "…" placeholder, replaced once the column loads.
      var preSel = slot.querySelector('[data-compare-model]');
      if (preSel) {
        var ph0 = preSel.querySelector('option[value=""]');
        if (ph0) ph0.remove();
        var pre = false;
        for (var p = 0; p < preSel.options.length; p++) {
          if (preSel.options[p].value === handle) { preSel.selectedIndex = p; pre = true; break; }
        }
        if (!pre) {
          var lo = document.createElement('option');
          lo.value = '__loading__'; lo.textContent = '…';
          preSel.insertBefore(lo, preSel.firstChild); preSel.selectedIndex = 0;
        }
        this.syncModelTrigger(slot);
      }
      return fetchColumn(handle).then(function (col) {
        body.classList.remove('is-loading');
        if (!col) {
          var previousColumn = body.querySelector('.cmp-col--product');
          var failedPlaceholder = preSel && preSel.querySelector('option[value="__loading__"]');
          if (failedPlaceholder) failedPlaceholder.remove();
          if (previousColumn && preSel) {
            for (var restoreIndex = 0; restoreIndex < preSel.options.length; restoreIndex++) {
              if (preSel.options[restoreIndex].dataset.id === previousColumn.dataset.model) {
                preSel.selectedIndex = restoreIndex;
                break;
              }
            }
            self.syncModelTrigger(slot);
          } else if (!previousColumn) {
            slot.remove();
          }
          self.dedupeDropdowns();
          self.ensureAddSlot();
          self.afterRender({ syncState: false });
          toast(self.cfg.labels && self.cfg.labels.loadError || 'Could not load that product. Please try again.');
          return 'failed';
        }
        body.innerHTML = '';
        var columnName = col.querySelector('.cmp-col__model-name') || col.querySelector('.cmp-col__name');
        var columnFamily = familyFor({
          handle: col.dataset.handle,
          title: columnName ? columnName.textContent.trim() : '',
          type: col.dataset.category || '',
          typeLabel: col.dataset.category || ''
        });
        col.dataset.category = columnFamily.key;
        col.dataset.categoryLabel = columnFamily.label;

        // Do not trust the query string as a family guard. A manually composed or
        // stale shared URL must not mix unrelated purchase decisions (for example
        // Mac and iPhone) even though the storewide tray already prevents it.
        var existingFamily = self.productColumns()
          .filter(function (existing) { return existing.closest('.cmp-slot') !== slot; })
          .map(function (existing) { return existing.dataset.category || ''; })
          .filter(Boolean)[0];
        if (existingFamily && columnFamily.key && existingFamily !== columnFamily.key) {
          slot.remove();
          toast(text('sameCategory', 'Choose products from the same product family to compare them.'));
          return 'rejected';
        }

        // Reusing a model family is valid: shoppers often need Mac mini vs Mac mini
        // or two MacBook configurations. If the seed configuration is already in
        // another column, automatically open the first unused sibling configuration
        // instead of creating a visually identical duplicate.
        var usedHandles = self.productColumns()
          .filter(function (existing) { return existing.closest('.cmp-slot') !== slot; })
          .map(function (existing) { return existing.dataset.handle || ''; })
          .filter(Boolean);
        if (usedHandles.indexOf(col.dataset.handle) !== -1) {
          var configurationControls = [].slice.call(col.querySelectorAll('.cmp-col__storages [data-compare-switch]'));
          if (!configurationControls.length) {
            configurationControls = [].slice.call(col.querySelectorAll('.cmp-col__swatches [data-compare-switch]'));
          }
          var unusedConfiguration = configurationControls
            .map(function (control) { return control.getAttribute('data-compare-switch'); })
            .filter(Boolean)
            .filter(function (candidate) { return usedHandles.indexOf(candidate) === -1; })[0];
          if (unusedConfiguration && unusedConfiguration !== col.dataset.handle) {
            return self.loadInto(slot, unusedConfiguration);
          }

          body.classList.remove('is-loading');
          var existingColumn = body.querySelector('.cmp-col--product');
          if (!existingColumn) slot.remove();
          self.dedupeDropdowns();
          self.ensureAddSlot();
          self.afterRender({ syncState: false });
          toast(self.cfg.labels && self.cfg.labels.allConfigurationsUsed || 'All available configurations of this model are already selected.');
          return 'rejected';
        }

        body.appendChild(col);
        var sel = slot.querySelector('[data-compare-model]');
        if (sel) {
          // placeholders no longer needed once filled
          var ph = sel.querySelector('option[value=""]');
          if (ph) ph.remove();
          var lph = sel.querySelector('option[value="__loading__"]');
          if (lph) lph.remove();
          var id = col.dataset.model;
          var matched = false;
          for (var i = 0; i < sel.options.length; i++) {
            if (sel.options[i].dataset.id === id) { sel.selectedIndex = i; matched = true; break; }
          }
          if (!matched) {
            // Loaded product isn't one of the (merchant-configured) models - e.g. a
            // different category reached via the storewide compare. Inject its real
            // name so the dropdown shows the actual product, not the first config model.
            var nameEl = col.querySelector('.cmp-col__model-name') || col.querySelector('.cmp-col__name');
            var opt = document.createElement('option');
            opt.value = col.dataset.handle || '';
            opt.dataset.id = id || '';
            opt.dataset.type = col.dataset.category || '';
            opt.textContent = nameEl ? nameEl.textContent.trim() : (col.dataset.handle || '');
            sel.insertBefore(opt, sel.firstChild);
            sel.selectedIndex = 0;
          }

          // The dropdown represents a model family, while the column can show a
          // specific configuration from that family. Keep the menu taxonomy clean,
          // but make the active choice unambiguous (for example Mac mini M4 with
          // 16GB/256GB versus the same model with 24GB/512GB).
          var activeOption = sel.options[sel.selectedIndex];
          var pickerModelName = col.dataset.modelName || '';
          var pickerConfiguration = col.dataset.configuration || '';
          if (activeOption && pickerModelName) {
            activeOption.textContent = pickerModelName + (pickerConfiguration ? ' · ' + pickerConfiguration : '');
            sel.title = activeOption.textContent;
            sel.setAttribute('aria-label', activeOption.textContent);
          }
          self.syncModelTrigger(slot);
        }
        self.dedupeDropdowns();
        self.ensureAddSlot();
        self.afterRender();
        return 'loaded';
      });
    };

    CT.prototype.ensureAddSlot = function () {
      // Keep exactly one trailing empty slot while under max. A model family can be
      // selected more than once because each column may resolve to a different
      // concrete configuration; loadInto prevents duplicate handles.
      var empties = [].slice.call(this.slots.querySelectorAll('.cmp-slot--empty'));
      empties.forEach(function (s) { s.remove(); });
      var curType = this.currentType();
      var addable = (this.cfg.models || []).some(function (m) {
        return !curType || !m.type || m.type === curType;
      });
      if (this.productColumns().length < this.max && addable) {
        var addSlot = this.makeSlot(true);
        this.slots.appendChild(addSlot);
        // The add-slot is created AFTER dedupeDropdowns runs, so filter its fresh
        // select here too - otherwise it would list every category (e.g. iPhones
        // and Macs while comparing AirPods).
        var addSel = addSlot.querySelector('[data-compare-model]');
        if (addSel) {
          this.filterSelect(addSel, curType);
          this.syncModelTrigger(addSlot);
        }
      }
      this.empty.hidden = this.productColumns().length > 0;
    };

    CT.prototype.onClick = function (e) {
      var modelTrigger = e.target.closest('[data-compare-model-trigger]');
      if (modelTrigger) {
        this.openModelPicker(modelTrigger);
        return;
      }
      var railStep = e.target.closest('[data-compare-rail-step]');
      if (railStep) {
        var railWrap = railStep.closest('.cmp-option-rail');
        var rail = railWrap && railWrap.querySelector('.cmp-col__storages');
        if (!rail) return;
        var step = parseInt(railStep.getAttribute('data-compare-rail-step'), 10) || 1;
        if (getComputedStyle(rail).direction === 'rtl') step *= -1;
        rail.scrollBy({
          left: step * Math.max(112, rail.clientWidth * 0.82),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
        return;
      }
      // Clicking the dashed "+ Add model" placeholder opens its model dropdown.
      var addBox = e.target.closest('.cmp-slot--empty .cmp-slot__body');
      if (addBox) {
        var addTrigger = addBox.closest('.cmp-slot').querySelector('[data-compare-model-trigger]');
        if (addTrigger) this.openModelPicker(addTrigger);
        return;
      }
      var sw = e.target.closest('[data-compare-switch]');
      if (sw) {
        if (sw.getAttribute('aria-disabled') === 'true' || sw.disabled) return;
        var s1 = sw.closest('.cmp-slot');
        if (s1) this.loadInto(s1, sw.getAttribute('data-compare-switch'));
        return;
      }
      var rm = e.target.closest('[data-compare-remove]');
      if (rm) {
        var s2 = rm.closest('.cmp-slot');
        if (s2) { s2.remove(); this.dedupeDropdowns(); this.ensureAddSlot(); this.afterRender(); }
      }
    };

    CT.prototype.onChange = function (e) {
      var sel = e.target.closest('[data-compare-model]');
      if (sel && sel.value) {
        this.syncModelTrigger(sel.closest('.cmp-slot'));
        this.loadInto(sel.closest('.cmp-slot'), sel.value);
      }
    };

    /* ------------------------- drag-to-reorder columns ----------------------- */
    CT.prototype.onReorderKeydown = function (e) {
      var handle = e.target.closest && e.target.closest('[data-compare-drag]');
      if (!handle || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
      var slot = handle.closest('.cmp-slot');
      if (!slot || slot.classList.contains('cmp-slot--empty')) return;

      var columns = [].slice.call(this.slots.querySelectorAll('.cmp-slot:not(.cmp-slot--empty)'));
      var index = columns.indexOf(slot);
      var rtl = getComputedStyle(this.slots).direction === 'rtl';
      var visualStep = e.key === 'ArrowRight' ? 1 : -1;
      var step = rtl ? -visualStep : visualStep;
      var target = columns[index + step];
      if (!target) return;

      e.preventDefault();
      if (step < 0) this.slots.insertBefore(slot, target);
      else this.slots.insertBefore(slot, target.nextSibling);
      this.afterRender();
      handle.focus();

      var name = slot.querySelector('.cmp-col__model-name') || slot.querySelector('.cmp-col__name');
      var moved = this.cfg.labels && this.cfg.labels.movedTemplate || '{product} moved.';
      announce(interpolate(moved, { product: name ? name.textContent.trim() : '' }));
    };

    // Pointer-based (mouse AND touch alike - native HTML5 DnD is unreliable on touch).
    // Model: a floating clone of the column (its blue pill + header) follows the cursor.
    // The source column stays put as a faint "lifted from here" gap. Columns DON'T move
    // mid-drag; instead, the neighbour the card is hovering over is highlighted as the
    // drop target. On drop, the dragged column and the highlighted target swap places
    // with a one-shot FLIP animation. Clear and jitter-free.
    CT.prototype.onDragStart = function (e) {
      if (e.button != null && e.button !== 0) return; // primary button / touch only
      var handle = e.target.closest('[data-compare-drag]');
      if (!handle) return;
      var slot = handle.closest('.cmp-slot');
      if (!slot || slot.classList.contains('cmp-slot--empty')) return;
      var prod = [].slice.call(this.slots.querySelectorAll('.cmp-slot:not(.cmp-slot--empty)'));
      if (prod.length < 2) return; // nothing to reorder
      e.preventDefault();

      var rect = slot.getBoundingClientRect();
      var picker = slot.querySelector('.cmp-slot__picker');
      var header = slot.querySelector('.cmp-col__header');
      var ghost = document.createElement('div');
      ghost.className = 'cmp-drag-ghost';
      ghost.style.width = rect.width + 'px';
      if (picker) {
        var pclone = picker.cloneNode(true);
        // cloneNode doesn't carry a <select>'s runtime selection, so the pill would show
        // the first option. Copy the live value across so it shows the right model.
        var origSel = picker.querySelector('select');
        var cloneSel = pclone.querySelector('select');
        if (origSel && cloneSel) cloneSel.value = origSel.value;
        ghost.appendChild(pclone);
      }
      ghost.appendChild((header || slot).cloneNode(true));
      document.body.appendChild(ghost);
      document.body.classList.add('cmp-dragging');
      slot.classList.add('cmp-slot--placeholder');

      // The mobile grid uses CSS scroll-snap; applying transforms mid-drag makes the
      // browser re-snap and jump the scroll. Turn snap off for the drag and restore the
      // scroll position when it's over.
      var grid = this.querySelector('.cmp-grid');
      if (grid) grid.style.scrollSnapType = 'none';

      this._drag = {
        slot: slot, ghost: ghost, prod: prod, target: null,
        offX: e.clientX - rect.left, offY: e.clientY - rect.top, w: rect.width,
        grid: grid, scrollLeft: grid ? grid.scrollLeft : 0
      };
      this._moveGhost(e.clientX, e.clientY);

      // Listen on document, NOT the handle: a captured element implicitly drops its
      // pointer capture if moved in the DOM, which would cut off events.
      this._onDragMove = this.onDragMove.bind(this);
      this._onDragEnd = this.onDragEnd.bind(this);
      document.addEventListener('pointermove', this._onDragMove);
      document.addEventListener('pointerup', this._onDragEnd);
      document.addEventListener('pointercancel', this._onDragEnd);
    };

    CT.prototype._moveGhost = function (x, y) {
      var d = this._drag; if (!d) return;
      d.ghost.style.transform = 'translate(' + (x - d.offX) + 'px,' + (y - d.offY) + 'px) scale(1.02)';
    };

    CT.prototype.onDragMove = function (e) {
      var d = this._drag; if (!d) return;
      this._moveGhost(e.clientX, e.clientY);
      // Drop target = the neighbour the floating card OVERLAPS the most (robust when the
      // wide card overshoots a neighbour's centre). Needs a meaningful overlap so it only
      // commits to a target once the card is clearly over it.
      var cardLeft = e.clientX - d.offX, cardRight = cardLeft + d.w;
      var target = null, best = d.w * 0.2;
      d.prod.forEach(function (s) {
        if (s === d.slot) return;
        var r = s.getBoundingClientRect();
        var ov = Math.min(cardRight, r.right) - Math.max(cardLeft, r.left);
        if (ov > best) { best = ov; target = s; }
      });
      if (target === d.target) return;
      if (d.target) d.target.classList.remove('cmp-slot--drop-target');
      d.target = target;
      if (target) target.classList.add('cmp-slot--drop-target');
    };

    CT.prototype.onDragEnd = function () {
      var d = this._drag; if (!d) return;
      document.removeEventListener('pointermove', this._onDragMove);
      document.removeEventListener('pointerup', this._onDragEnd);
      document.removeEventListener('pointercancel', this._onDragEnd);
      this._drag = null;

      var self = this, ghost = d.ghost, slot = d.slot, target = d.target;
      var grid = d.grid, savedSL = d.scrollLeft;
      if (target) target.classList.remove('cmp-slot--drop-target');

      var ghostRect = ghost.getBoundingClientRect(); // where the card is right now
      // Remember the displaced column's current spot (for its slide) before we touch DOM.
      var dispFrom = target ? target.getBoundingClientRect().left : 0;

      // Commit the swap in the DOM first (no animation yet).
      if (target) {
        var marker = document.createComment('');
        this.slots.replaceChild(marker, slot);
        this.slots.replaceChild(slot, target);
        this.slots.replaceChild(target, marker);
      }

      // Hand the real column off from the ghost: reveal it, pin it under the ghost, drop
      // the ghost, then glide the WHOLE column (specs and all) to its slot. The displaced
      // column slides the opposite way at the same time. Both are real → fully smooth.
      slot.classList.remove('cmp-slot--placeholder');
      var rest = slot.getBoundingClientRect();
      var dx = ghostRect.left - rest.left, dy = ghostRect.top - rest.top;
      slot.style.transition = 'none';
      slot.style.transform = 'translate(' + dx + 'px,' + dy + 'px)';
      if (ghost.parentNode) ghost.remove();

      var disp = target;
      if (disp) {
        var ddx = dispFrom - disp.getBoundingClientRect().left;
        disp.style.transition = 'none';
        disp.style.transform = 'translateX(' + ddx + 'px)';
      }

      requestAnimationFrame(function () {
        var ease = 'transform 0.26s cubic-bezier(0.2,0.8,0.2,1)';
        slot.style.transition = ease; slot.style.transform = '';
        if (disp) { disp.style.transition = ease; disp.style.transform = ''; }
      });

      var done = false;
      var finish = function () {
        if (done) return; done = true;
        slot.style.transition = ''; slot.style.transform = '';
        if (disp) { disp.style.transition = ''; disp.style.transform = ''; }
        document.body.classList.remove('cmp-dragging');
        // Restore the scroll position, then re-enable snap (so it doesn't jump-snap).
        if (grid) { grid.scrollLeft = savedSL; grid.style.scrollSnapType = ''; }
        self.dedupeDropdowns();
        self.afterRender();
      };
      slot.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 320);
    };

    // Highlight the whole row (label + every column) on hover.
    CT.prototype.onRowHover = function (e) {
      var cell = e.target.closest('[data-row]');
      var on = e.type === 'pointerover';
      if (this._hoverRow && (!cell || cell.getAttribute('data-row') !== this._hoverRow)) {
        var prev = this._hoverRow;
        this.querySelectorAll('[data-row="' + prev + '"]').forEach(function (x) { x.classList.remove('cmp-row--hover'); });
        this._hoverRow = null;
      }
      if (on && cell) {
        var row = cell.getAttribute('data-row');
        this._hoverRow = row;
        this.querySelectorAll('[data-row="' + row + '"]').forEach(function (x) { x.classList.add('cmp-row--hover'); });
      }
    };

    // Filter ONE model dropdown to the current comparison: hide models from a
    // different category entirely (so an AirPods comparison never lists iPhones).
    // Do not disable a used model family: multiple concrete configurations of the
    // same Mac mini, MacBook or iPhone are a meaningful comparison.
    CT.prototype.filterSelect = function (sel, curType) {
      [].slice.call(sel.options).forEach(function (o) {
        if (!o.value) return;
        // Different category → REMOVE from the DOM (not just `hidden`). iOS Safari's
        // native <select> picker ignores [hidden] and would list every product; removing
        // scopes the picker to the current category, matching desktop. Category is fixed
        // for the lifetime of a compare page, so removed options never need to return.
        if (curType && o.dataset.type && o.dataset.type !== curType) { o.remove(); return; }
        o.disabled = false;
      });
    };

    CT.prototype.dedupeDropdowns = function () {
      var self = this;
      var curType = this.currentType();
      this.slots.querySelectorAll('.cmp-slot').forEach(function (slot) {
        var sel = slot.querySelector('[data-compare-model]');
        if (sel) {
          self.filterSelect(sel, curType);
          self.syncModelTrigger(slot);
        }
      });
    };

    // A concrete configuration may only appear in one column. Keep the selected
    // configuration visible in its own column and remove it from every sibling rail;
    // a disabled duplicate wastes scarce horizontal space and looks selectable.
    CT.prototype.dedupeConfigSwitches = function () {
      var columns = this.productColumns();
      var usedHandles = columns.map(function (c) { return c.dataset.handle; });
      columns.forEach(function (column) {
        var ownHandle = column.dataset.handle;
        column.querySelectorAll('[data-compare-switch]').forEach(function (control) {
          var target = control.getAttribute('data-compare-switch');
          var taken = target !== ownHandle && usedHandles.indexOf(target) !== -1;
          control.disabled = taken;
          control.classList.toggle('is-unavailable', taken);
          control.setAttribute('aria-disabled', taken ? 'true' : 'false');
          control.hidden = taken;
        });
      });
    };

    // Keep the shareable URL and the storewide tray aligned with the concrete
    // configurations currently visible after switch, add, remove or drag reorder.
    CT.prototype.syncSelectionState = function () {
      var columns = this.productColumns();
      var items = columns.map(function (column) {
        var name = column.querySelector('.cmp-col__name');
        var modelNameElement = column.querySelector('.cmp-col__model-name') || name;
        var modelName = column.dataset.modelName || (modelNameElement ? modelNameElement.textContent.trim() : '') || (column.dataset.handle || '');
        var selectedConfig = column.querySelector('[data-compare-switch][aria-pressed="true"]');
        var configName = column.dataset.configuration || (selectedConfig ? selectedConfig.textContent.trim() : '');
        var displayTitle = modelName + (configName ? ' · ' + configName : '');
        var image = column.querySelector('.cmp-col__media img');
        var link = name && name.querySelector('a');
        return {
          id: column.dataset.handle || '',
          handle: column.dataset.handle || '',
          title: displayTitle,
          modelTitle: modelName,
          image: image ? (image.currentSrc || image.src || '') : '',
          url: link ? link.getAttribute('href') || '' : '',
          type: column.dataset.category || '',
          typeLabel: column.dataset.categoryLabel || column.dataset.category || '',
          family: column.dataset.category || '',
          group: column.dataset.model || ''
        };
      }).filter(function (item) { return item.handle; }).slice(0, this.max);

      var handles = items.map(function (item) { return item.handle; });
      var current = read();
      var currentHandles = current.map(function (item) { return item.handle; });
      var detailsChanged = current.length !== items.length || current.some(function (item, index) {
        var next = items[index] || {};
        return item.handle !== next.handle || item.title !== next.title || item.image !== next.image ||
          item.type !== next.type || item.group !== next.group;
      });
      if (currentHandles.join(',') !== handles.join(',') || detailsChanged) write(items);

      try {
        var url = new URL(location.href);
        var productParam = handles.join(',');
        if (productParam) url.searchParams.set('products', productParam);
        else url.searchParams.delete('products');
        if (url.href !== location.href) history.replaceState(history.state, '', url.href);
      } catch (e) {}
    };

    // The single purchase-decision family shared by every loaded column (e.g. "mac"),
    // or '' if the columns are mixed / none loaded yet. Drives heading + dropdown filtering.
    CT.prototype.currentType = function () {
      var cats = this.productColumns()
        .map(function (c) { return c.dataset.category || ''; })
        .filter(Boolean);
      if (!cats.length) return '';
      var first = cats[0];
      return cats.every(function (c) { return c === first; }) ? first : '';
    };

    CT.prototype.currentTypeLabel = function () {
      var type = this.currentType();
      if (!type) return '';
      var first = this.productColumns().filter(function (c) { return c.dataset.category === type; })[0];
      return first && first.dataset.categoryLabel || type;
    };

    // Replace the {type} token with the friendly live comparison-family label.
    CT.prototype.updateHeading = function () {
      var h = document.querySelector('[data-compare-heading]');
      if (!h) return;
      var tpl = h.getAttribute('data-compare-heading') || '';
      if (tpl.indexOf('{type}') === -1) return;
      var type = this.currentTypeLabel() || h.getAttribute('data-compare-fallback') || 'products';
      h.textContent = tpl.split('{type}').join(type);
    };

    CT.prototype.afterRender = function (options) {
      this.setTableWidth();
      this.dedupeConfigSwitches();
      if (!this._initializingColumns && (!options || options.syncState !== false)) {
        this.syncSelectionState();
      }
      this.syncRowHeights();
      this.applyDiff();
      this.hideEmptyRows();
      this.hideEmptyGroups();
      this.applyZebra();
      this.updateHeading();
      this.setupScrollRails();
      this.renderSticky();
    };

    /* Configuration and colour choices frequently exceed a comparison column's
       width. Native touch scrolling remains intact; this adds desktop grab-to-scroll,
       keyboard scrolling and visible overflow affordances without changing the
       buttons' click behaviour. */
    CT.prototype.setupScrollRails = function () {
      var self = this;
      var rails = [].slice.call(this.querySelectorAll('.cmp-col__storages, .cmp-col__swatches'));
      var jump = document.querySelector('.cmp-jump');
      if (jump) rails.push(jump);

      if (!this._scrollRailObserver && 'ResizeObserver' in window) {
        this._scrollRailObserver = new ResizeObserver(function (entries) {
          entries.forEach(function (entry) { self.updateScrollRail(entry.target); });
        });
      }

      rails.forEach(function (rail) {
        rail.classList.add('cmp-scroll-rail');
        self.updateScrollRail(rail);
        if (self._scrollRailObserver) self._scrollRailObserver.observe(rail);

        var selected = rail.querySelector('.is-selected, [aria-pressed="true"]');
        var selectedKey = selected && (selected.dataset.handle || selected.value || selected.textContent.trim());
        if (selected && rail.dataset.cmpCenteredSelection !== selectedKey) {
          rail.dataset.cmpCenteredSelection = selectedKey || 'selected';
          var pageX = window.scrollX;
          var pageY = window.scrollY;
          selected.scrollIntoView({ block: 'nearest', inline: 'center' });
          window.scrollTo(pageX, pageY);
        }

        if (rail.dataset.cmpDragScrollReady === 'true') return;
        rail.dataset.cmpDragScrollReady = 'true';

        var drag = null;
        var suppressClick = false;
        rail.addEventListener('pointerdown', function (event) {
          if (!rail.classList.contains('is-scrollable') || (event.pointerType && event.pointerType !== 'mouse' && event.pointerType !== 'pen')) return;
          if (event.button !== 0) return;
          drag = { id: event.pointerId, x: event.clientX, scroll: rail.scrollLeft, moved: false };
        });
        rail.addEventListener('pointermove', function (event) {
          if (!drag || drag.id !== event.pointerId) return;
          var delta = event.clientX - drag.x;
          if (!drag.moved && Math.abs(delta) < 5) return;
          if (!drag.moved) {
            drag.moved = true;
            rail.classList.add('is-dragging');
            rail.setPointerCapture(event.pointerId);
          }
          event.preventDefault();
          rail.scrollLeft = drag.scroll - delta;
          self.updateScrollRail(rail);
        });
        function endDrag(event) {
          if (!drag || drag.id !== event.pointerId) return;
          suppressClick = drag.moved;
          drag = null;
          rail.classList.remove('is-dragging');
          if (rail.hasPointerCapture && rail.hasPointerCapture(event.pointerId)) rail.releasePointerCapture(event.pointerId);
          if (suppressClick) window.setTimeout(function () { suppressClick = false; }, 0);
        }
        rail.addEventListener('pointerup', endDrag);
        rail.addEventListener('pointercancel', endDrag);
        rail.addEventListener('click', function (event) {
          if (!suppressClick) return;
          event.preventDefault();
          event.stopPropagation();
        }, true);
        rail.addEventListener('scroll', function () { self.updateScrollRail(rail); }, { passive: true });
        rail.addEventListener('keydown', function (event) {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          var direction = event.key === 'ArrowRight' ? 1 : -1;
          if (getComputedStyle(rail).direction === 'rtl') direction *= -1;
          rail.scrollBy({
            left: direction * Math.max(96, rail.clientWidth * 0.72),
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
          });
          event.preventDefault();
        });
      });
    };

    CT.prototype.updateScrollRail = function (rail) {
      var overflow = rail.scrollWidth > rail.clientWidth + 2;
      rail.classList.toggle('is-scrollable', overflow);
      rail.classList.toggle('is-at-start', !overflow || Math.abs(rail.scrollLeft) < 2);
      var max = Math.max(0, rail.scrollWidth - rail.clientWidth);
      rail.classList.toggle('is-at-end', !overflow || Math.abs(rail.scrollLeft) >= max - 2);
      if (overflow) {
        if (!rail.hasAttribute('tabindex')) rail.setAttribute('tabindex', '0');
      } else if (rail.getAttribute('tabindex') === '0') {
        rail.removeAttribute('tabindex');
      }
      var optionWrap = rail.closest('.cmp-option-rail');
      if (optionWrap) {
        optionWrap.classList.toggle('is-scrollable', overflow);
        var previous = optionWrap.querySelector('[data-compare-rail-step="-1"]');
        var next = optionWrap.querySelector('[data-compare-rail-step="1"]');
        var atStart = rail.classList.contains('is-at-start');
        var atEnd = rail.classList.contains('is-at-end');
        if (getComputedStyle(rail).direction === 'rtl') {
          if (previous) previous.disabled = !overflow || atEnd;
          if (next) next.disabled = !overflow || atStart;
        } else {
          if (previous) previous.disabled = !overflow || atStart;
          if (next) next.disabled = !overflow || atEnd;
        }
      }
    };

    CT.prototype.disconnectedCallback = function () {
      if (this._onSticky) {
        window.removeEventListener('scroll', this._onSticky);
        var grid = this.querySelector('.cmp-grid');
        if (grid) grid.removeEventListener('scroll', this._onSticky);
      }
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      if (this._scrollRailObserver) this._scrollRailObserver.disconnect();
    };

    // Width the full-width spec-group banners should span: the columns' total width,
    // but never wider than the visible grid (so on mobile they stay viewport-width and
    // don't trigger page scroll). Exposed as --cmp-tablew for the CSS banner.
    CT.prototype.setTableWidth = function () {
      var grid = this.querySelector('.cmp-grid');
      if (!grid) return;
      var content = 0;
      var label = grid.querySelector('.cmp-col--labels');
      if (label) content += label.offsetWidth;
      this.querySelectorAll('.cmp-slots .cmp-slot').forEach(function (s) { content += s.offsetWidth; });
      var w = Math.min(content, grid.clientWidth);
      this.style.setProperty('--cmp-tablew', w + 'px');
    };

    // Stripe alternate VISIBLE rows. Done in JS (not :nth-child) because group
    // titles interleave the cells and columns load independently - a shared class
    // keyed by data-row keeps the stripe aligned across every column.
    CT.prototype.applyZebra = function () {
      var cols = this.productColumns();
      var idx = 0;
      this.querySelectorAll('.cmp-col--labels [data-row]').forEach(function (label) {
        var row = label.getAttribute('data-row');
        var cells = [label];
        cols.forEach(function (c) { var x = c.querySelector('[data-row="' + row + '"]'); if (x) cells.push(x); });
        var alt;
        if (label.classList.contains('is-hidden')) {
          alt = false;
        } else {
          alt = (idx % 2) === 1; idx++;
        }
        cells.forEach(function (x) { x.classList.toggle('cmp-row--alt', alt); });
      });
    };

    CT.prototype.syncRowHeights = function () {
      var groups = {};
      this.querySelectorAll('[data-row],[data-grouprow]').forEach(function (el) {
        var k = el.getAttribute('data-row') || ('g:' + el.getAttribute('data-grouprow'));
        (groups[k] = groups[k] || []).push(el);
      });
      Object.keys(groups).forEach(function (k) {
        var els = groups[k];
        els.forEach(function (el) { el.style.height = 'auto'; });
        var h = 0;
        els.forEach(function (el) { h = Math.max(h, el.offsetHeight); });
        els.forEach(function (el) { el.style.height = h + 'px'; });
      });

      // Header content is made of independent semantic rows. Synchronize each row,
      // not just the total header height, so a colour swatch or wrapped configuration
      // in one column never pushes only that column's price and CTA downward.
      var productColumns = this.productColumns();
      [
        '.cmp-col__media',
        '.cmp-col__name',
        '.cmp-col__tag',
        '.cmp-col__swatches',
        '.cmp-col__storages',
        '.cmp-col__price',
        '.cmp-col__cta'
      ].forEach(function (selector) {
        var rowElements = productColumns.map(function (column) { return column.querySelector(selector); }).filter(Boolean);
        if (!rowElements.length) return;
        rowElements.forEach(function (element) {
          if (selector === '.cmp-col__media') element.style.removeProperty('height');
          else element.style.height = 'auto';
          element.classList.remove('cmp-header-row--collapsed');
        });
        var hasContent = rowElements.some(function (element) { return !element.classList.contains('is-empty'); });
        if (!hasContent) {
          rowElements.forEach(function (element) {
            element.classList.add('cmp-header-row--collapsed');
            element.style.height = '0px';
          });
          return;
        }
        var rowHeight = 0;
        rowElements.forEach(function (element) { rowHeight = Math.max(rowHeight, element.offsetHeight); });
        rowElements.forEach(function (element) { element.style.height = rowHeight + 'px'; });
      });
      var heads = [].slice.call(this.querySelectorAll('.cmp-col__header'));
      heads.forEach(function (el) { el.style.height = 'auto'; });
      var mh = 0; heads.forEach(function (el) { mh = Math.max(mh, el.offsetHeight); });
      heads.forEach(function (el) { el.style.height = mh + 'px'; });
    };

    CT.prototype.eachRow = function (cb) {
      var cols = this.productColumns();
      this.querySelectorAll('.cmp-col--labels [data-row]').forEach(function (label) {
        var row = label.getAttribute('data-row');
        var cells = cols.map(function (c) { return c.querySelector('[data-row="' + row + '"]'); })
          .filter(function (c) { return c; });
        cb(label, cells);
      });
    };

    CT.prototype.hideEmptyRows = function () {
      this.eachRow(function (label, cells) {
        var allEmpty = cells.length > 0 && cells.every(function (c) { return c.getAttribute('data-empty') === 'true'; });
        label.classList.toggle('is-hidden', allEmpty);
        cells.forEach(function (c) { c.classList.toggle('is-hidden', allEmpty); });
      });
    };

    // Hide a whole spec group's banner (e.g. "Display", "Camera") when every row
    // under it is hidden - otherwise an AirPods comparison shows stray group titles
    // with nothing beneath them. The group list is the same for every category (it's
    // the spec schema in compare-spec.liquid); this just drops the groups that don't
    // apply to the loaded products, by walking the label column in document order:
    // each [data-grouprow] opens a group, the [data-row]s until the next one belong
    // to it. The title + every column's spacer share the data-grouprow, so toggle all.
    CT.prototype.hideEmptyGroups = function () {
      var self = this;
      var specs = this.querySelector('.cmp-col--labels .cmp-col__specs');
      if (!specs) return;
      var currentGroup = null, rows = [];
      var flush = function () {
        if (!currentGroup) return;
        var g = currentGroup.getAttribute('data-grouprow');
        var hide = rows.length > 0 && rows.every(function (r) { return r.classList.contains('is-hidden'); });
        self.querySelectorAll('[data-grouprow="' + g + '"]').forEach(function (el) {
          el.classList.toggle('is-hidden', hide);
        });
      };
      [].slice.call(specs.children).forEach(function (el) {
        if (el.hasAttribute('data-grouprow')) { flush(); currentGroup = el; rows = []; }
        else if (el.hasAttribute('data-row')) { rows.push(el); }
      });
      flush();
    };

    CT.prototype.applyDiff = function () {
      var diffEl = document.querySelector('[data-compare-diff]');
      var on = !!(diffEl && (diffEl.checked || diffEl.getAttribute('aria-pressed') === 'true'));
      this.eachRow(function (label, cells) {
        var diff = false;
        if (on && cells.length > 1) {
          var norm = cells.map(function (c) { return c.textContent.replace(/\s+/g, ' ').trim(); });
          diff = !norm.every(function (v) { return v === norm[0]; });
        }
        label.classList.toggle('cmp-diff-hit', on && diff);
        cells.forEach(function (c) { c.classList.toggle('cmp-diff-hit', on && diff); });
      });
      this.classList.toggle('cmp-table--diffing', on);
    };

    return CT;
  })();

  if (!customElements.get('compare-table')) customElements.define('compare-table', CompareTable);

  function debounce(fn, ms) {
    var h; return function () { var a = arguments, t = this; clearTimeout(h); h = setTimeout(function () { fn.apply(t, a); }, ms); };
  }
})();
